import { useState } from 'react';
import { t } from '../../i18n/index.js';
import { useSession } from '../../lib/session.js';
import { useApiData } from '../../lib/use-data.js';
import { formatDate, formatDateTime } from '../../lib/format.js';
import { DataTable, type Column } from '../../patterns/DataTable.js';
import { useTableView } from '../../patterns/table-view.js';
import { PageHeader } from '../../patterns/PageHeader.js';
import { Badge, type BadgeIntent } from '../../ui/Badge.js';
import { Banner } from '../../ui/Banner.js';
import { Button } from '../../ui/Button.js';
import { ApiError, type AdminUser } from '../../lib/api.js';
import { AddAdminDialog } from './AddAdminDialog.js';

/**
 * Who may act across every account.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * READABLE BY EVERY RUNG, CHANGEABLE BY ONE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A `support` administrator sees this list in full. That is deliberate: a
 * roster is not a secret from the people on it, and hiding it makes "who did
 * this" unanswerable by exactly the people who need to ask.
 *
 * Only `super_admin` may change it, enforced in the database. The controls
 * below are absent rather than disabled for anybody else — a disabled button
 * invites "why not", an absent one does not claim the action exists.
 */
const STATUS_INTENT: Record<string, BadgeIntent> = {
  active: 'success',
  suspended: 'warning',
  revoked: 'danger',
};

export function AdminsPage() {
  const { api, me } = useSession();
  const { data, loading, error, reload } = useApiData(() => api.admins(), [api]);
  const [adding, setAdding] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const maySteer = me?.role === 'super_admin';

  const columns: Column<AdminUser>[] = [
    {
      id: 'email',
      header: t('admins.person'),
      sort: (r) => r.email,
      search: (r) => `${r.email} ${r.name ?? ''}`,
      cell: (r) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-primary" title={r.email}>
            {r.email}
          </span>
          {r.name ? <span className="text-sm text-tertiary">{r.name}</span> : null}
        </div>
      ),
    },
    {
      id: 'role',
      header: t('admins.role'),
      sort: (r) => r.role,
      cell: (r) => <Badge intent="info">{r.role}</Badge>,
    },
    {
      id: 'status',
      header: t('admins.status'),
      sort: (r) => r.status,
      cell: (r) => (
        <div className="flex items-center gap-2">
          <Badge intent={STATUS_INTENT[r.status] ?? 'neutral'}>{r.status}</Badge>
          {/*
            The bootstrap admin is marked, because the reason its controls are
            missing is a property of the ROW rather than of the reader. Without
            this the page looks like it failed to render them.
          */}
          {r.isBootstrap ? <Badge intent="neutral">{t('admins.bootstrap')}</Badge> : null}
        </div>
      ),
    },
    {
      id: 'lastLogin',
      header: t('admins.lastSignIn'),
      nowrap: true,
      sort: (r) => r.lastLoginAt ?? '',
      cell: (r) =>
        r.lastLoginAt ? (
          formatDateTime(r.lastLoginAt)
        ) : (
          <span className="text-tertiary">{t('admins.neverSignedIn')}</span>
        ),
    },
    {
      id: 'created',
      header: t('admins.added'),
      nowrap: true,
      secondary: true,
      sort: (r) => r.createdAt,
      cell: (r) => formatDate(r.createdAt),
    },
    {
      id: 'actions',
      header: t('admins.actions'),
      headerHidden: true,
      align: 'right',
      width: '1%',
      cell: (r) => {
        /*
          Three rows never get a control, for three different reasons, and
          every one of them is also enforced server-side:

            the bootstrap admin  a database trigger refuses the change
            yourself            `admin_roster_set_status` refuses it, and it
                                is the one account you could lock yourself out
            not a super_admin   the role check, in the database

          Hiding a control the platform would refuse is a courtesy. It is not
          the control.
        */
        if (!maySteer || r.isBootstrap || r.email === me?.email) return null;
        const next = r.status === 'active' ? 'suspended' : 'active';
        return (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setFailure(null);
              void api
                .setAdminStatus(r.id, next)
                .then(() => reload())
                .catch((err: unknown) => {
                  setFailure(err instanceof ApiError ? err.message : t('admins.changeFailed'));
                });
            }}
          >
            {next === 'suspended' ? t('admins.suspend') : t('admins.reactivate')}
          </Button>
        );
      },
    },
  ];

  const view = useTableView(data ?? [], {
    columns,
    searchPlaceholder: t('admins.search'),
    itemNoun: t('admins.noun'),
  });

  return (
    <>
      <PageHeader
        title={t('admins.title')}
        description={t('admins.description')}
        actions={
          maySteer ? (
            <Button variant="primary" onClick={() => setAdding(true)}>
              {t('admins.add')}
            </Button>
          ) : null
        }
      />

      {failure ? (
        <Banner intent="danger" title={t('admins.changeRefusedTitle')}>
          {failure}
        </Banner>
      ) : null}

      <DataTable
        view={view}
        loading={loading}
        error={error}
        onRetry={reload}
        rowKey={(r) => r.id}
        isRowMuted={(r) => r.status !== 'active'}
        /*
          This table is never legitimately empty: migration 0325 seeds the
          bootstrap admin in every environment. So an empty roster means the
          request came back wrong rather than that nobody has been added, and
          'not-available' says that where 'nothing-yet' would suggest the
          ordinary state of a new install.
        */
        emptyReason="not-available"
        emptyTitle={t('admins.emptyTitle')}
        emptyDescription={t('admins.emptyBody')}
      />

      {adding ? (
        <AddAdminDialog
          onCancel={() => setAdding(false)}
          onAdd={async (input) => {
            await api.addAdmin(input);
            setAdding(false);
            void reload();
          }}
        />
      ) : null}
    </>
  );
}
